import { useEffect } from 'react';
import { useUI, useEditorState } from '../contexts';

type UseFileContentParams = {
  activeProject: string;
  token: string | null;
  apiBase: string;
  authFetch: (url: string, options?: RequestInit, needAuth?: boolean) => Promise<Response>;
  persistedFiles: Record<string, string>;
  workingFiles: Record<string, string>;
  fetchedFiles: Record<string, boolean>;
  resolvePath: (id: string) => string;
  setPersistedFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setWorkingFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setFetchedFiles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
};

export function useFileContent({
  activeProject,
  token,
  apiBase,
  authFetch,
  persistedFiles,
  workingFiles,
  fetchedFiles,
  resolvePath,
  setPersistedFiles,
  setWorkingFiles,
  setFetchedFiles,
}: UseFileContentParams) {
  // Get UI state from context
  const { setStatus, setGlobalError } = useUI();

  // Get editor state from context
  const { activeFile, setCode } = useEditorState();
  useEffect(() => {
    if (!activeFile || !token || !apiBase) return;

    const cached = workingFiles[activeFile] ?? persistedFiles[activeFile];
    if (cached !== undefined) {
      setCode(cached);
      setStatus(`Önbellek · ${resolvePath(activeFile)}`);
      setFetchedFiles((prev) => ({ ...prev, [activeFile]: true }));
      return;
    }

    if (fetchedFiles[activeFile]) return;

    const controller = new AbortController();
    const fetchContent = async () => {
      setStatus(`İçerik yükleniyor · ${resolvePath(activeFile)}`);
      try {
        const res = await authFetch(
          `${apiBase}/flow/gitlike-get-file-content`,
          {
            method: 'POST',
            body: JSON.stringify({ fileID: activeFile, projectID: activeProject }),
            signal: controller.signal,
          },
          true
        );
        const data = await res.json();
        console.log('file-content response', data);
        if (data?.status) {
          const content =
            data.result?.content ?? data.result?.file?.content ?? data.result?.data ?? data.result?.body ?? '';
          setPersistedFiles((prev) => ({ ...prev, [activeFile]: content }));
          setWorkingFiles((prev) => ({ ...prev, [activeFile]: content }));
          setFetchedFiles((prev) => ({ ...prev, [activeFile]: true }));
          setCode(content);
          setStatus('Dosya yüklendi');
        } else {
          setStatus('İçerik alınamadı');
          setGlobalError('İçerik alınamadı');
          setCode('');
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Content error', error);
        setStatus('İçerik hatası');
        setGlobalError('İçerik hatası');
      }
    };
    fetchContent();
    return () => controller.abort();
  }, [
    activeFile,
    activeProject,
    authFetch,
    apiBase,
    fetchedFiles,
    persistedFiles,
    resolvePath,
    token,
    workingFiles,
    setStatus,
    setGlobalError,
    setPersistedFiles,
    setWorkingFiles,
    setFetchedFiles,
    setCode,
  ]);
}
